// Shared response handling for the live business accounting API
// (/api/v1/business/*): { data, meta } success envelope and
// { error: { code, message, details } } error envelope (PRD 00 §8.3).

import { ApiErrorDetail, ApiMeta } from '../types/businessApi';
import { notifyBusinessReadOnly } from './businessReadOnlyNotifier';
import { notifyBusinessPeriodLock } from './businessPeriodLockNotifier';
import { notifyFirmRequired } from './businessFirmRequiredNotifier';
import { notifyRoleForbidden } from './businessRoleForbiddenNotifier';
import { requestOriginOf } from '../utils/businessRequestOrigin';

/** Error code every business-scoped mutation gets on a lapsed business. */
export const BUSINESS_READ_ONLY_CODE = 'business_read_only';

/** Error code every mutation gets when its date falls in a closed period. */
export const PERIOD_LOCKED_CODE = 'period_locked';

/**
 * Error code a document void gets when its original period is closed (JC-12).
 * Not a failure to fix but a question: the reversal would post today instead,
 * and the caller confirms by resending with `confirm_reversal_date_fallback`.
 */
export const REVERSAL_DATE_FALLBACK_CODE = 'reversal_date_fallback_required';

/**
 * Error code an accountant or bookkeeper gets on EVERY business-scoped call —
 * reads included — until they belong to a firm (PRD 40 §5.5.1, revised).
 */
export const FIRM_REQUIRED_CODE = 'firm_required';

/**
 * Error code a dispatcher or technician gets on EVERY business action nobody
 * named for their role — reads included (PRD 89 §4.9, RestrictedRoleGate).
 * Distinct from `forbidden`, which is one action's own role check: this one
 * means the whole page is not theirs, so the shell says so once, for the page.
 */
export const ROLE_FORBIDDEN_CODE = 'role_forbidden';

export class BusinessApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiErrorDetail[];

  constructor(status: number, code: string, message: string, details: ApiErrorDetail[] = []) {
    super(message);
    this.name = 'BusinessApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Parse a business API response. Throws BusinessApiError (with the server's
 * code/message/details when present) on non-2xx responses.
 */
export async function parseEnvelope<T>(response: Response): Promise<{ data: T; meta: ApiMeta }> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // 204 / empty bodies — leave body null
  }

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string; details?: ApiErrorDetail[] } } | null)?.error;
    // Lapse-policy safety net: any mutation the UI failed to gate still gets
    // the consistent read-only toast (LiveBusinessShell subscribes).
    if (response.status === 403 && error?.code === BUSINESS_READ_ONLY_CODE) {
      notifyBusinessReadOnly();
    }
    // Same safety net for period locking: the server carries the closing date
    // in details so the shell can refresh its cached status (PRD 08 §8.5).
    if (error?.code === PERIOD_LOCKED_CODE) {
      const details = error.details as unknown as { closed_through?: string } | undefined;
      notifyBusinessPeriodLock({
        closedThrough: details?.closed_through ?? null,
        message: error.message ?? 'That period is closed.',
      });
    }
    // Accountant HQ is mandatory to open a client's books. Unlike the two
    // above this is not a safety net for a gate the UI should have applied
    // itself: the client cannot know the answer, because "does this person
    // belong to a firm" is a fact about the user and not about the screen.
    if (response.status === 403 && error?.code === FIRM_REQUIRED_CODE) {
      notifyFirmRequired();
    }
    // A field role reached a page that is not part of its role. Like the one
    // above, not a gate the UI forgot: the shell replaces the page with one
    // sentence, and it needs to know WHICH page asked, because the answer can
    // arrive after the person has moved on (utils/businessRequestOrigin).
    if (response.status === 403 && error?.code === ROLE_FORBIDDEN_CODE) {
      const origin = requestOriginOf(response);
      notifyRoleForbidden({
        path: origin?.path ?? null,
        businessId: origin?.businessId ?? null,
        message: error.message ?? "This isn't part of your role here",
      });
    }
    throw new BusinessApiError(
      response.status,
      error?.code ?? 'http_error',
      error?.message ?? `Request failed (${response.status})`,
      error?.details ?? []
    );
  }

  const envelope = body as { data?: T; meta?: ApiMeta } | null;
  return { data: envelope?.data as T, meta: envelope?.meta ?? {} };
}

/** Like parseEnvelope, but returns only the data payload. */
export async function parseData<T>(response: Response): Promise<T> {
  const { data } = await parseEnvelope<T>(response);
  return data;
}

/** Build a query string ("?a=1&b=2") from defined params; "" when empty. */
export function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}
